import InlineError from "./InlineError";

// Checkbox — presentation primitive. Native <input type="checkbox">
// wrapped in a token-driven label affordance. Selector-free,
// dispatch-free; consumes its state via props only.
//
// Props:
//   id:          string — required for label/control association
//   name:        string — passed through to the underlying <input>
//   label:       node   — visible label text or composition
//   description: node   — optional secondary text below the label
//   checked:     bool   — controlled value
//   onChange:    (event) => void — receives the native change event
//   disabled:    bool   — disables interaction
//   error:       node   — optional inline error to render
//   className, ...rest

function Checkbox({
  id,
  name,
  label,
  description,
  checked = false,
  onChange,
  disabled = false,
  error,
  className = '',
  ...rest
}) {
  const errorId = error && id ? `${id}-error` : undefined;
  const descriptionId = description && id ? `${id}-description` : undefined;
  const describedBy = [errorId, descriptionId].filter(Boolean).join(' ') || undefined;

  const classes = [
    'ui-checkbox',
    disabled ? 'ui-checkbox--disabled' : '',
    error ? 'ui-checkbox--invalid' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <label className={classes} htmlFor={id}>
      <input
        id={id}
        name={name}
        type="checkbox"
        className="ui-checkbox__input"
        checked={Boolean(checked)}
        onChange={onChange}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={error ? 'true' : undefined}
        {...rest}
      />
      <span className="ui-checkbox__indicator" aria-hidden="true">
        <svg
          className="ui-checkbox__check"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M3 8.5l3 3 7-7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="ui-checkbox__text">
        {label ? <span className="ui-checkbox__label">{label}</span> : null}
        {description ? (
          <span id={descriptionId} className="ui-checkbox__description">
            {description}
          </span>
        ) : null}
        {error ? (
          <InlineError id={errorId} className="ui-checkbox__error">
            {error}
          </InlineError>
        ) : null}
      </span>
    </label>
  );
}

export default Checkbox;
